import { BookOpen, Users, User, Calendar, Filter, AlertTriangle, CheckCircle2, XCircle, Circle, TrendingUp, BarChart2, FileText, ArrowLeft, Eye, Loader2, BookX, ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react';
import DepartmentSelector from '../../../../components/selectors/DepartmentSelector';
import UserSelector from '../../../../components/selectors/UserSelector';
import { ApiService } from '../../../../services/api';
import { useState, useEffect, useMemo } from 'react';
import checkPermission from '../../../../utils/permission';

interface AvailableMonth {
    year: number;
    month: number;
}

interface DepartmentReportSummary {
    year: number;
    month: number;
    totalStudents: number;
    studentsWithBooklets: number;
    noBookletStudents: number;
    completedBooklets: number;
    inProgressBooklets: number;
    pendingBooklets: number;
    totalQuestions: number;
    totalSolved: number;
    totalCorrect: number;
    totalWrong: number;
    averageSuccessRate: number;
}

interface StudentReportItem {
    userId: string;
    firstName: string;
    lastName: string;
    schoolNumber?: string;
    department?: { id: string; name: string; color: string };
    hasBooklet: boolean;
    bookletId: string | null;
    totalQuestions: number;
    solvedQuestions: number;
    correctAnswers: number;
    wrongAnswers: number;
    emptyAnswers: number;
    status: string;
    completedAt: string | null;
    successRate: number;
}

interface StudentDetailReport {
    user: {
        id: string;
        firstName: string;
        lastName: string;
        schoolNumber?: string;
        department?: { id: string; name: string; color: string };
    };
    year: number;
    month: number;
    hasBooklet: boolean;
    booklet: {
        id: string;
        status: string;
        totalQuestions: number;
        solvedQuestions: number;
        correctAnswers: number;
        wrongAnswers: number;
        emptyAnswers: number;
        successRate: number;
        startedAt: string | null;
        completedAt: string | null;
    } | null;
    questions: Array<{
        id: string;
        order: number;
        lessonName: string;
        lessonId: string;
        sectionName: string;
        gain: string | null;
        answer: any;
        isCorrect: boolean | null;
        previousWrongAnswer: any;
        correctOption: any;
        questionContent: string | null;
        questionMedia: { id: string; url: string; type: string } | null;
    }>;
    lessonSummary: Array<{
        lessonId: string;
        lessonName: string;
        totalQuestions: number;
        correctAnswers: number;
        wrongAnswers: number;
        emptyAnswers: number;
        successRate: number;
    }>;
}

interface PaginationMeta {
    totalItems: number;
    totalPages: number;
    currentPage: number;
    perPage: number;
}

const STATUS_OPTIONS = [
    { value: '', label: 'Tümü' },
    { value: 'PENDING', label: 'Bekliyor' },
    { value: 'IN_PROGRESS', label: 'Yapılıyor' },
    { value: 'COMPLETED', label: 'Tamamlandı' },
];

const monthNames = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
];

export default function ErrorBookletReportsTab() {
    const hasExportPermission = checkPermission("homework_report_list");

    const [availableMonths, setAvailableMonths] = useState<AvailableMonth[]>([]);
    const [selectedMonth, setSelectedMonth] = useState<AvailableMonth | null>(null);
    const [selectedDepartment, setSelectedDepartment] = useState<string | null>(null);
    const [selectedStudent, setSelectedStudent] = useState<string | null>(null);
    const [selectedStatus, setSelectedStatus] = useState<string>('');

    const [departmentReport, setDepartmentReport] = useState<{
        summary: DepartmentReportSummary;
        data: StudentReportItem[];
        meta: PaginationMeta;
    } | null>(null);

    const [studentDetailReport, setStudentDetailReport] = useState<StudentDetailReport | null>(null);

    const [loading, setLoading] = useState(false);
    const [loadingMonths, setLoadingMonths] = useState(true);
    const [filtered, setFiltered] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [viewMode, setViewMode] = useState<'list' | 'detail'>('list');
    const [filterType, setFilterType] = useState<'department' | 'student'>('department');

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(20);
    const [resetting, setResetting] = useState(false);
    const [showResetConfirm, setShowResetConfirm] = useState(false);

    useEffect(() => {
        loadAvailableMonths();
    }, []);

    const loadAvailableMonths = async () => {
        try {
            setLoadingMonths(true);

            const months = await ApiService.getErrorBookletAvailableMonths();
            setAvailableMonths(months);

            if (months.length > 0) setSelectedMonth(months[0]);
        } catch (error) {
            console.error('Error loading available months:', error);
        } finally {
            setLoadingMonths(false);
        }
    };

    const loadReport = async (page: number = 1) => {
        if (!selectedMonth) return;

        try {
            setLoading(true);
            setError(null);

            if (selectedStudent) {
                const report = await ApiService.getErrorBookletStudentReport(
                    selectedStudent,
                    selectedMonth.year,
                    selectedMonth.month
                );

                setStudentDetailReport(report);
                setDepartmentReport(null);
                setViewMode('detail');
            } else if (selectedDepartment) {
                const report = await ApiService.getErrorBookletDepartmentReport(
                    selectedDepartment,
                    selectedMonth.year,
                    selectedMonth.month,
                    {
                        page,
                        limit: pageSize,
                        statuses: selectedStatus ? [selectedStatus] : undefined,
                    }
                );

                setDepartmentReport(report);
                setCurrentPage(page);
                setStudentDetailReport(null);
                setViewMode('list');
            };
        } catch (err: any) {
            console.error('Error loading report:', err);
            setError(err?.message || 'Rapor yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    const handleViewStudentDetail = async (userId: string) => {
        if (!selectedMonth) return;

        try {
            setLoading(true);

            const report = await ApiService.getErrorBookletStudentReport(
                userId,
                selectedMonth.year,
                selectedMonth.month
            );

            setStudentDetailReport(report);
            setViewMode('detail');
        } catch (error) {
            console.error('Error loading student detail:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleBackToList = () => {
        setStudentDetailReport(null);
        setSelectedStudent(null);
        setViewMode('list');
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'COMPLETED':
                return (
                    <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full inline-flex items-center justify-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        Tamamlandı
                    </span>
                );
            case 'IN_PROGRESS':
                return (
                    <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full inline-flex items-center justify-center gap-1">
                        <Circle className="w-3 h-3" />
                        Devam Ediyor
                    </span>
                );
            case 'PENDING':
                return (
                    <span className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full inline-flex items-center justify-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Bekliyor
                    </span>
                );
            case 'NO_BOOKLET':
                return (
                    <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full inline-flex items-center justify-center gap-1">
                        <BookX className="w-3 h-3" />
                        Kitapçık Yok
                    </span>
                );
            default:
                return null;
        }
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const isFilterReady = () => {
        if (!selectedMonth) return false;
        if (filterType === 'department') return !!selectedDepartment;
        return !!selectedStudent;
    };

    const buildMissingMessage = () => {
        const parts: string[] = [];
        if (!selectedMonth) parts.push("Dönem");
        if (filterType === 'department' && !selectedDepartment) parts.push("Departman");
        if (filterType === 'student' && !selectedStudent) parts.push("Öğrenci");
        if (parts.length === 0) return "";
        if (parts.length === 1) return `${parts[0]} seçiniz.`;
        return `${parts[0]} ve ${parts[1]} seçiniz.`;
    };

    const handleFilter = () => {
        if (isFilterReady()) {
            setFiltered(true);
            loadReport();
        }
    };

    const handleClearFilters = () => {
        setSelectedDepartment(null);
        setSelectedStudent(null);
        setSelectedStatus('');
        setCurrentPage(1);
        setDepartmentReport(null);
        setStudentDetailReport(null);
        setError(null);
        setFiltered(false);
        setViewMode('list');
    };

    const handleDepartmentChange = (deptId: string | null) => {
        setSelectedDepartment(deptId);
        if (deptId) setSelectedStudent(null);
    };

    const handleStudentChange = (studentId: string | null) => {
        setSelectedStudent(studentId);
        if (studentId) setSelectedDepartment(null);
    };

    const handleResetBooklet = async () => {
        if (!studentDetailReport || !studentDetailReport.hasBooklet || !selectedMonth) return;

        try {
            setResetting(true);

            await ApiService.resetStudentErrorBooklet(
                studentDetailReport.user.id,
                selectedMonth.year,
                selectedMonth.month
            );

            const report = await ApiService.getErrorBookletStudentReport(
                studentDetailReport.user.id,
                selectedMonth.year,
                selectedMonth.month
            );

            setStudentDetailReport(report);
            setShowResetConfirm(false);
        } catch (err: any) {
            console.error('Error resetting booklet:', err);

            setError(err?.message || 'Kitapçık sıfırlanamadı');
        } finally {
            setResetting(false);
        }
    };

    const groupedQuestions = useMemo(() => {
        if (!studentDetailReport?.questions) return {};

        const groups: { [key: string]: typeof studentDetailReport.questions } = {};
        for (const q of studentDetailReport.questions) {
            const lessonId = q.lessonId || 'other';

            if (!groups[lessonId]) groups[lessonId] = [];
            groups[lessonId].push(q);
        }

        return groups;
    }, [studentDetailReport]);

    return (
        <div className="h-full flex flex-col min-h-0">
            {!filtered && (
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3 sm:p-6 mb-6 overflow-visible">
                    <div className="flex items-center gap-3 mb-4">
                        <Filter className="w-5 h-5 text-gray-600" />
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900">Filtreler</h3>
                    </div>

                    <div className="overflow-visible">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6 overflow-visible">
                            <div className="col-span-1">
                                <label className="flex items-center gap-2 text-xs sm:text-sm font-medium text-gray-700 mb-2">
                                    <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                    Dönem
                                </label>
                                {loadingMonths ? (
                                    <div className="flex items-center justify-center py-4 border border-gray-300 rounded-lg">
                                        <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                                    </div>
                                ) : availableMonths.length === 0 ? (
                                    <div className="px-3 py-2 text-sm text-gray-500 border border-gray-300 rounded-lg">
                                        Henüz hata kitapçığı bulunmuyor.
                                    </div>
                                ) : (
                                    <div className="border border-gray-300 rounded-lg overflow-hidden">
                                        <div className="max-h-64 overflow-auto divide-y divide-gray-200">
                                            {availableMonths.map((m) => {
                                                const isSelected = selectedMonth?.year === m.year && selectedMonth?.month === m.month;
                                                return (
                                                    <button
                                                        key={`${m.year}-${m.month}`}
                                                        type="button"
                                                        onClick={() => setSelectedMonth(m)}
                                                        className={`w-full text-left px-3 py-2 text-xs sm:text-sm transition-colors ${isSelected ? 'bg-blue-50 text-blue-900 font-medium' : 'hover:bg-gray-50 text-gray-800'
                                                            }`}
                                                    >
                                                        {monthNames[m.month - 1]} {m.year}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="col-span-1">
                                <label className="flex items-center gap-2 text-xs sm:text-sm font-medium text-gray-700 mb-2">
                                    Rapor Türü
                                </label>
                                <div className="border border-gray-300 rounded-lg overflow-hidden">
                                    <div className="divide-y divide-gray-200">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setFilterType('department');
                                                setSelectedStudent(null);
                                            }}
                                            className={`w-full text-left px-3 py-2 text-xs sm:text-sm transition-colors ${filterType === 'department'
                                                ? 'bg-blue-50 text-blue-900 font-medium'
                                                : 'hover:bg-gray-50 text-gray-800'
                                                }`}
                                        >
                                            Departman
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setFilterType('student');
                                                setSelectedDepartment(null);
                                            }}
                                            className={`w-full text-left px-3 py-2 text-xs sm:text-sm transition-colors ${filterType === 'student'
                                                ? 'bg-blue-50 text-blue-900 font-medium'
                                                : 'hover:bg-gray-50 text-gray-800'
                                                }`}
                                        >
                                            Öğrenci
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="col-span-1">
                                {filterType === 'department' ? (
                                    <>
                                        <label className="flex items-center gap-2 text-xs sm:text-sm font-medium text-gray-700 mb-2">
                                            <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                            Departman Seçin
                                        </label>
                                        <DepartmentSelector
                                            value={selectedDepartment}
                                            onChange={handleDepartmentChange}
                                            placeholder="Departman Seçin"
                                            allowClear={true}
                                            showColors={true}
                                            className="text-sm"
                                        />
                                    </>
                                ) : (
                                    <>
                                        <label className="flex items-center gap-2 text-xs sm:text-sm font-medium text-gray-700 mb-2">
                                            <User className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                            Öğrenci Seçin
                                        </label>
                                        <UserSelector
                                            value={selectedStudent}
                                            onChange={handleStudentChange}
                                            placeholder="Öğrenci Seçin"
                                            allowClear={true}
                                            className="text-sm"
                                        />
                                    </>
                                )}
                            </div>

                            {filterType === 'department' && (
                                <div className="col-span-1">
                                    <label className="flex items-center gap-2 text-xs sm:text-sm font-medium text-gray-700 mb-2">
                                        Durum Filtresi
                                    </label>
                                    <div className="border border-gray-300 rounded-lg overflow-hidden">
                                        <div className="divide-y divide-gray-200">
                                            {STATUS_OPTIONS.map((option) => {
                                                const isSelected = selectedStatus === option.value;
                                                return (
                                                    <button
                                                        key={option.value || 'all'}
                                                        type="button"
                                                        onClick={() => setSelectedStatus(option.value)}
                                                        className={`w-full text-left px-3 py-2 text-xs sm:text-sm transition-colors ${isSelected
                                                            ? 'bg-blue-50 text-blue-900 font-medium'
                                                            : 'hover:bg-gray-50 text-gray-800'
                                                            }`}
                                                    >
                                                        {option.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 mt-3 sm:mt-4">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
                            <button
                                onClick={handleFilter}
                                disabled={!isFilterReady()}
                                className={`px-4 sm:px-6 py-2 rounded-lg font-medium text-sm transition-colors ${isFilterReady() ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-gray-300 text-gray-500 cursor-not-allowed"
                                    }`}
                            >
                                Filtrele
                            </button>
                            {!isFilterReady() && (
                                <span className="text-xs text-gray-600">Raporları görüntülemek için lütfen {buildMissingMessage()}</span>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {filtered && (
                <div className="grid grid-cols-1 md:grid-cols-3 items-center mb-6 gap-4">
                    <div className="flex items-center space-x-4 justify-start">
                        <div className="flex flex-row flex-wrap gap-2">
                            {viewMode === 'detail' && departmentReport && (
                                <button
                                    onClick={handleBackToList}
                                    className="px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium border flex-shrink-0 inline-flex items-center justify-center gap-1.5 sm:gap-2 bg-white text-gray-700 hover:bg-gray-50 border-gray-300 transition-colors"
                                >
                                    <ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                    <span>Geri</span>
                                </button>
                            )}
                            <button
                                onClick={handleClearFilters}
                                className="px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium border flex-shrink-0 inline-flex items-center justify-center gap-1.5 sm:gap-2 bg-white text-gray-700 hover:bg-gray-50 border-gray-300 transition-colors"
                            >
                                <Filter className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                <span>Filtrele</span>
                            </button>
                        </div>
                    </div>
                    <div />
                    {departmentReport && viewMode === 'list' && (
                        <div className="flex items-center space-x-4 justify-start md:justify-end">
                            <div className="flex items-center space-x-2 text-sm text-gray-600">
                                <span>
                                    {departmentReport.meta.totalItems > 0
                                        ? `${(currentPage - 1) * pageSize + 1}-${Math.min(currentPage * pageSize, departmentReport.meta.totalItems)} / ${departmentReport.meta.totalItems}`
                                        : "0 / 0"}
                                </span>
                                <button
                                    onClick={() => loadReport(currentPage - 1)}
                                    disabled={currentPage <= 1 || loading}
                                    className="disabled:opacity-50 disabled:cursor-not-allowed p-1 hover:bg-gray-100 rounded"
                                >
                                    <ChevronLeft className="h-4 w-4 text-gray-400" />
                                </button>
                                <button
                                    onClick={() => loadReport(currentPage + 1)}
                                    disabled={currentPage >= departmentReport.meta.totalPages || loading}
                                    className="disabled:opacity-50 disabled:cursor-not-allowed p-1 hover:bg-gray-100 rounded"
                                >
                                    <ChevronRight className="h-4 w-4 text-gray-400" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {filtered && error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                    <p className="text-sm font-medium text-red-900">Hata</p>
                    <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
            )}

            <div className="flex-1 min-h-0 overflow-y-auto">
                {!filtered ? (
                    <div className="flex-1 flex items-center justify-center h-full"></div>
                ) : loading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                        <span className="ml-3 text-gray-600">Rapor yükleniyor...</span>
                    </div>
                ) : error ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Hata Oluştu</h3>
                        <p className="text-gray-500 max-w-md mb-4">{error}</p>
                        <button
                            onClick={() => {
                                setError(null);
                                handleFilter();
                            }}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Tekrar Dene
                        </button>
                    </div>
                ) : viewMode === 'list' && departmentReport ? (
                    <>
                        <div className="mb-6 bg-white rounded-lg border border-gray-200 shadow-sm p-5">
                            <div className="flex items-center gap-2 mb-4">
                                <BarChart2 className="w-5 h-5 text-indigo-600" />
                                <h3 className="text-lg font-semibold text-gray-900">
                                    {monthNames[departmentReport.summary.month - 1]} {departmentReport.summary.year} - Özet
                                </h3>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                                <div className="bg-white rounded-lg p-4 shadow-sm">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                                            <Users className="w-5 h-5 text-indigo-600" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500">Toplam Öğrenci</p>
                                            <p className="text-xl font-bold text-gray-900">
                                                {departmentReport.summary.totalStudents}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white rounded-lg p-4 shadow-sm">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                            <BookOpen className="w-5 h-5 text-blue-600" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500">Kitapçığı Olan</p>
                                            <p className="text-xl font-bold text-gray-900">
                                                {departmentReport.summary.studentsWithBooklets}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white rounded-lg p-4 shadow-sm">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500">Tamamlanan</p>
                                            <p className="text-xl font-bold text-gray-900">
                                                {departmentReport.summary.completedBooklets}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white rounded-lg p-4 shadow-sm">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                                            <FileText className="w-5 h-5 text-amber-600" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500">Toplam Soru</p>
                                            <p className="text-xl font-bold text-gray-900">
                                                {departmentReport.summary.totalQuestions}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white rounded-lg p-4 shadow-sm">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                            <TrendingUp className="w-5 h-5 text-purple-600" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500">Ortalama Başarı</p>
                                            <p className="text-xl font-bold text-gray-900">
                                                %{departmentReport.summary.averageSuccessRate}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-4 pt-4 border-t border-gray-200">
                                <div className="flex items-center gap-6">
                                    <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 bg-green-500 rounded" />
                                        <span className="text-sm text-gray-600">Doğru: {departmentReport.summary.totalCorrect}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 bg-red-500 rounded" />
                                        <span className="text-sm text-gray-600">Yanlış: {departmentReport.summary.totalWrong}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 bg-gray-300 rounded" />
                                        <span className="text-sm text-gray-600">
                                            Çözülmemiş: {departmentReport.summary.totalQuestions - departmentReport.summary.totalSolved}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                            <div className="block md:hidden divide-y divide-gray-200">
                                {departmentReport.data.map((student: StudentReportItem) => (
                                    <div key={student.userId} className="p-4 space-y-3">
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <User className="w-4 h-4 text-indigo-600" />
                                                    <p className="font-semibold text-gray-900">
                                                        {student.firstName} {student.lastName}
                                                    </p>
                                                </div>
                                                {student.schoolNumber && (
                                                    <p className="text-sm text-gray-600">No: {student.schoolNumber}</p>
                                                )}
                                            </div>
                                            <div className="flex flex-col items-end gap-1">
                                                {getStatusBadge(student.status)}
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 text-sm">
                                            <div>
                                                <span className="text-gray-500">Toplam:</span>
                                                <span className="ml-1 text-gray-900 font-medium">{student.totalQuestions}</span>
                                            </div>
                                            <div>
                                                <span className="text-gray-500">Çözülen:</span>
                                                <span className="ml-1 text-blue-600 font-medium">{student.solvedQuestions}</span>
                                            </div>
                                            <div>
                                                <span className="text-gray-500">Doğru:</span>
                                                <span className="ml-1 text-green-600 font-medium">{student.correctAnswers}</span>
                                            </div>
                                            <div>
                                                <span className="text-gray-500">Yanlış:</span>
                                                <span className="ml-1 text-red-600 font-medium">{student.wrongAnswers}</span>
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                                            <div>
                                                {student.hasBooklet && student.solvedQuestions > 0 ? (
                                                    <span className={`font-bold ${student.successRate >= 80 ? 'text-green-600' :
                                                        student.successRate >= 60 ? 'text-amber-600' :
                                                            'text-red-600'
                                                        }`}>
                                                        %{student.successRate} Başarı
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-400">-</span>
                                                )}
                                            </div>
                                            {student.hasBooklet && (
                                                <button
                                                    onClick={() => handleViewStudentDetail(student.userId)}
                                                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors"
                                                >
                                                    <Eye className="w-3.5 h-3.5" />
                                                    Detay
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="hidden md:block overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 text-xs">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Öğrenci</th>
                                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Durum</th>
                                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Toplam</th>
                                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Çözülen</th>
                                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Doğru</th>
                                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Yanlış</th>
                                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Boş</th>
                                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Başarı</th>
                                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">İşlem</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {departmentReport.data.map((student: StudentReportItem, idx: number) => (
                                            <tr key={student.userId} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                                                            <User className="w-4 h-4 text-indigo-600" />
                                                        </div>
                                                        <div>
                                                            <p className="font-medium text-gray-900">
                                                                {student.firstName} {student.lastName}
                                                            </p>
                                                            {student.schoolNumber && (
                                                                <p className="text-xs text-gray-500">{student.schoolNumber}</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    {getStatusBadge(student.status)}
                                                </td>
                                                <td className="px-4 py-3 text-center font-medium text-gray-700">
                                                    {student.totalQuestions}
                                                </td>
                                                <td className="px-4 py-3 text-center font-medium text-blue-600">
                                                    {student.solvedQuestions}
                                                </td>
                                                <td className="px-4 py-3 text-center font-medium text-green-600">
                                                    {student.correctAnswers}
                                                </td>
                                                <td className="px-4 py-3 text-center font-medium text-red-600">
                                                    {student.wrongAnswers}
                                                </td>
                                                <td className="px-4 py-3 text-center font-medium text-gray-500">
                                                    {student.emptyAnswers}
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    {student.hasBooklet && student.solvedQuestions > 0 ? (
                                                        <span className={`font-bold ${student.successRate >= 80 ? 'text-green-600' :
                                                            student.successRate >= 60 ? 'text-amber-600' :
                                                                'text-red-600'
                                                            }`}>
                                                            %{student.successRate}
                                                        </span>
                                                    ) : (
                                                        <span className="text-gray-400">-</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    {student.hasBooklet && (
                                                        <button
                                                            onClick={() => handleViewStudentDetail(student.userId)}
                                                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors"
                                                        >
                                                            <Eye className="w-3.5 h-3.5" />
                                                            Detay
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                ) : viewMode === 'detail' && studentDetailReport ? (
                    <div className="space-y-6">
                        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                                        <User className="w-6 h-6 text-indigo-600" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-bold text-gray-900">
                                            {studentDetailReport.user.firstName} {studentDetailReport.user.lastName}
                                        </h2>
                                        <p className="text-sm text-gray-500">
                                            {monthNames[studentDetailReport.month - 1]} {studentDetailReport.year} - Hata Kitapçığı Raporu
                                        </p>
                                    </div>
                                </div>
                                {studentDetailReport.hasBooklet && (
                                    <button
                                        onClick={() => setShowResetConfirm(true)}
                                        disabled={resetting}
                                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-orange-600 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {resetting ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <RotateCcw className="w-4 h-4" />
                                        )}
                                        Kitapçığı Sıfırla
                                    </button>
                                )}
                            </div>
                        </div>

                        {showResetConfirm && (
                            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                                <div className="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                                            <AlertTriangle className="w-5 h-5 text-orange-600" />
                                        </div>
                                        <h3 className="text-lg font-semibold text-gray-900">Kitapçığı Sıfırla</h3>
                                    </div>
                                    <p className="text-gray-600 mb-6">
                                        <strong>{studentDetailReport.user.firstName} {studentDetailReport.user.lastName}</strong> adlı öğrencinin{' '}
                                        <strong>{monthNames[studentDetailReport.month - 1]} {studentDetailReport.year}</strong> dönemine ait
                                        hata kitapçığındaki tüm cevapları sıfırlamak istediğinize emin misiniz?
                                        <br /><br />
                                        <span className="text-sm text-gray-500">Bu işlem geri alınamaz ve öğrenci kitapçığı tekrar çözmesi gerekecektir.</span>
                                    </p>
                                    <div className="flex items-center justify-end gap-3">
                                        <button
                                            onClick={() => setShowResetConfirm(false)}
                                            disabled={resetting}
                                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                                        >
                                            İptal
                                        </button>
                                        <button
                                            onClick={handleResetBooklet}
                                            disabled={resetting}
                                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
                                        >
                                            {resetting ? (
                                                <>
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                    Sıfırlanıyor...
                                                </>
                                            ) : (
                                                <>
                                                    <RotateCcw className="w-4 h-4" />
                                                    Sıfırla
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {!studentDetailReport.hasBooklet ? (
                            <div className="flex items-center justify-center py-16">
                                <div className="text-center">
                                    <BookX className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                    <h3 className="text-lg font-semibold text-gray-600 mb-2">
                                        Hata Kitapçığı Bulunamadı
                                    </h3>
                                    <p className="text-gray-500">
                                        Bu öğrencinin {monthNames[studentDetailReport.month - 1]} {studentDetailReport.year} döneminde hata kitapçığı bulunmamaktadır.
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-gray-500">Toplam Soru</span>
                                            <span className="text-xl font-bold text-gray-900">
                                                {studentDetailReport.booklet?.totalQuestions || 0}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="bg-white rounded-xl border border-green-200 p-4">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-gray-500">Doğru</span>
                                            <span className="text-xl font-bold text-green-600">
                                                {studentDetailReport.booklet?.correctAnswers || 0}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="bg-white rounded-xl border border-red-200 p-4">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-gray-500">Yanlış</span>
                                            <span className="text-xl font-bold text-red-600">
                                                {studentDetailReport.booklet?.wrongAnswers || 0}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="bg-white rounded-xl border border-amber-200 p-4">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-gray-500">Başarı Oranı</span>
                                            <span className={`text-xl font-bold ${(studentDetailReport.booklet?.successRate || 0) >= 80 ? 'text-green-600' :
                                                (studentDetailReport.booklet?.successRate || 0) >= 60 ? 'text-amber-600' :
                                                    'text-red-600'
                                                }`}>
                                                %{studentDetailReport.booklet?.successRate || 0}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
                                    <h4 className="font-semibold text-gray-800 mb-4">Ders Bazlı Özet</h4>
                                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                        {studentDetailReport.lessonSummary.map((lesson) => (
                                            <div
                                                key={lesson.lessonId}
                                                className="border border-gray-200 rounded-lg p-3"
                                            >
                                                <p className="font-medium text-gray-800 text-sm mb-2">{lesson.lessonName}</p>
                                                <div className="flex items-center justify-between text-xs">
                                                    <span className="text-gray-500">{lesson.totalQuestions} soru</span>
                                                    <span className={`font-bold ${lesson.successRate >= 80 ? 'text-green-600' :
                                                        lesson.successRate >= 60 ? 'text-amber-600' :
                                                            'text-red-600'
                                                        }`}>
                                                        %{lesson.successRate}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2 mt-2 text-xs">
                                                    <span className="text-green-600">D: {lesson.correctAnswers}</span>
                                                    <span className="text-red-600">Y: {lesson.wrongAnswers}</span>
                                                    <span className="text-gray-400">B: {lesson.emptyAnswers}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <h4 className="font-semibold text-gray-800">Sorular</h4>
                                    {Object.entries(groupedQuestions).map(([lessonId, questions]) => (
                                        <div key={lessonId} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                                            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                                                <h5 className="font-medium text-gray-800">{questions[0]?.lessonName || 'Diğer'}</h5>
                                            </div>
                                            <div className="divide-y divide-gray-100">
                                                {questions.map((q) => (
                                                    <div key={q.id} className="p-4">
                                                        <div className="flex items-start gap-4">
                                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${q.isCorrect === true ? 'bg-green-100' :
                                                                q.isCorrect === false ? 'bg-red-100' :
                                                                    'bg-gray-100'
                                                                }`}>
                                                                {q.isCorrect === true ? (
                                                                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                                                                ) : q.isCorrect === false ? (
                                                                    <XCircle className="w-4 h-4 text-red-600" />
                                                                ) : (
                                                                    <Circle className="w-4 h-4 text-gray-400" />
                                                                )}
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2 mb-1">
                                                                    <span className="text-sm font-medium text-gray-800">
                                                                        Soru {q.order}
                                                                    </span>
                                                                    {q.sectionName && (
                                                                        <span className="text-xs text-gray-500">({q.sectionName})</span>
                                                                    )}
                                                                </div>
                                                                {q.gain && (
                                                                    <p className="text-xs text-indigo-600 mb-2 bg-indigo-50 px-2 py-1 rounded inline-block">
                                                                        Kazanım: {q.gain}
                                                                    </p>
                                                                )}
                                                                <div className="flex items-center gap-4 text-sm">
                                                                    <div>
                                                                        <span className="text-gray-500">Önceki Yanlış: </span>
                                                                        <span className="font-medium text-orange-600">{q.previousWrongAnswer || '-'}</span>
                                                                    </div>
                                                                    <div>
                                                                        <span className="text-gray-500">Verilen Cevap: </span>
                                                                        <span className={`font-medium ${q.isCorrect === true ? 'text-green-600' :
                                                                            q.isCorrect === false ? 'text-red-600' :
                                                                                'text-gray-400'
                                                                            }`}>
                                                                            {q.answer || 'Boş'}
                                                                        </span>
                                                                    </div>
                                                                    <div>
                                                                        <span className="text-gray-500">Doğru Cevap: </span>
                                                                        <span className="font-medium text-green-600">{q.correctOption}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Veri Bulunamadı</h3>
                        <p className="text-gray-500 max-w-md mb-4">Seçilen kriterlere uygun rapor verisi bulunamadı.</p>
                        <button onClick={handleFilter} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                            Yenile
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}